import { SuccessOrError } from '@upright/common/types-api';
import { z } from 'zod';
import { DataPointTrace } from '../data-points/types';
import {
  CompanyGeographicalOperationsPoint,
  CompanyIndustry,
  CompanyIndustryOperationsPoint,
  CompanyOperationsDimension,
  CompanyOperationsMetricType,
} from './types';

const zOperationalDataPointBase = z.object({
  weight: z.number().min(0).max(1), // OH: Weights are stored as fractions while UI shows them as percentages.
  operationsMetricType: z.nativeEnum(CompanyOperationsMetricType),
  year: z.number(),
});

/**
 * Zod schema for a single datapoint on the industries the company operates in
 */
export const zCompanyOperationalIndustryDataPointInput = zOperationalDataPointBase.extend({
  key: z.nativeEnum(CompanyIndustry),
});
export type CompanyOperationalIndustryDataPointInput = z.infer<typeof zCompanyOperationalIndustryDataPointInput>;

/**
 * Zod schema for a single datapoint on the countries the company operates in
 */
export const zCompanyOperationalCountryDataPointInput = zOperationalDataPointBase.extend({
  key: z.string().min(1),
});
export type CompanyOperationalCountryDataPointInput = z.infer<typeof zCompanyOperationalCountryDataPointInput>;

/**
 * Zod schema for a single datapoint for either the industry or the country of the company
 */
export const zCompanyOperationalDataPointInput = z.union([
  zCompanyOperationalCountryDataPointInput,
  zCompanyOperationalIndustryDataPointInput,
]);

export type CompanyOperationalDataPointInput = z.infer<typeof zCompanyOperationalDataPointInput>;

/**
 * Request schema for operational industry data update
 * Note: `source` applies to all datapoints in the request and defaults to 'customer-dma-manual'.
 */
export const zReplaceOperationalDataPayload = z.object({
  operationsMetricType: z.nativeEnum(CompanyOperationsMetricType),
  dimension: z.nativeEnum(CompanyOperationsDimension),
  datapoints: z.array(zCompanyOperationalDataPointInput).min(0),
  source: z.string().optional(),
  trace: z.nativeEnum(DataPointTrace),
});

export type ReplaceOperationalDataRequestPayload = z.infer<typeof zReplaceOperationalDataPayload>;

/**
 * Request schema for getting operational data for a company
 */
export const zGetCompanyOperationalDataRequest = z.object({
  companyId: z.string(),
  operationsDimension: z.nativeEnum(CompanyOperationsDimension),
  year: z.coerce.number(),
});

export type GetCompanyOperationalDataRequest = z.infer<typeof zGetCompanyOperationalDataRequest>;

/**
 * Response returns success status
 */
export type ReplaceOperationalDataResponse = SuccessOrError;

/**
 * Response for GET request - returns all operational industry split datapoints for a company that the current user has
 * access to. UI expects the user to have access to only one source so the datapoint weights sum to max. 100% all together.
 */
export type GetOperationalDataResponse<T extends CompanyOperationsDimension> =
  T extends CompanyOperationsDimension.INDUSTRY
    ? {
        dataPoints: CompanyIndustryOperationsPoint[];
      }
    : { dataPoints: CompanyGeographicalOperationsPoint[] };

/**
 * Default source for all operational data from the UI. TODO PLT-3753: Add access a new datasource for each usergroup and
 * use it for filtering the datapoints user can read.
 */
export const DEFAULT_OPERATIONAL_DATA_SOURCE = 'customer-dma-manual';

// ---------------------------------------------------------------------------
// Agentic operational-data batch apply (PLT-5088)
// ---------------------------------------------------------------------------

/**
 * Upper bound on the number of distinct {dimension, operationsMetricType} datasets the agent may
 * propose in one chat card. There are only 2 dimensions × 4 metric types = 8 possible datasets, so
 * 8 is the natural ceiling; the bound exists to keep a misbehaving model from emitting an unbounded
 * card. Shared by the chat-only `propose_operational_data_batch` tool schema and the React card.
 */
export const MAX_OPERATIONAL_DATA_BATCH_DATASETS = 8;

/**
 * Upper bound on rows in a single proposed dataset. COUNTRY datasets enumerate country codes (the
 * larger case); INDUSTRY datasets are capped by the {@link CompanyIndustry} enum. 500 is comfortably
 * above any real split while still bounding a runaway proposal.
 */
export const MAX_OPERATIONAL_DATA_BATCH_ROWS = 500;

/**
 * A single row in an agentic operational-data batch. `key` is a country code (for the COUNTRY
 * dimension) or a {@link CompanyIndustry} value (for the INDUSTRY dimension); the cross-field
 * check below enforces the industry enum. `weight` is a fraction in [0, 1] (the card shows it as
 * a percentage), matching the manual write path.
 */
export const zOperationalDataBatchRow = z.object({
  key: z.string().min(1),
  weight: z.number().min(0).max(1),
});
export type OperationalDataBatchRow = z.infer<typeof zOperationalDataBatchRow>;

/**
 * Request body for `POST /api/company/{companyVariantId}/operational-data/batch` — exactly one
 * batch (one {dimension, operationsMetricType} dataset) per call. `batchId` is a ULID generated by
 * the agent per proposed batch and is the idempotency key: a repeated POST returns the stored
 * result instead of re-applying.
 *
 * `reportYear` is the year of the report the figures were read from. It is kept on the batch's audit
 * row only: the datapoints are always written for the current year, the same year the manual form
 * writes, so the Operational data tab shows them and the DMA uses them alongside manual input.
 */
export const zApplyOperationalDataBatchPayload = z
  .object({
    batchId: z.string().min(1),
    dataset: z.object({
      dimension: z.nativeEnum(CompanyOperationsDimension),
      operationsMetricType: z.nativeEnum(CompanyOperationsMetricType),
      reportYear: z.number().int().optional(),
    }),
    rows: z.array(zOperationalDataBatchRow).min(0),
    source: z.string().optional(),
  })
  .superRefine((payload, ctx) => {
    if (payload.dataset.dimension !== CompanyOperationsDimension.INDUSTRY) {
      return;
    }
    const validIndustries = new Set<string>(Object.values(CompanyIndustry));
    payload.rows.forEach((row, idx) => {
      if (!validIndustries.has(row.key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['rows', idx, 'key'],
          message: `Invalid industry key "${row.key}" for the industry dimension`,
        });
      }
    });
  });

export type ApplyOperationalDataBatchPayload = z.infer<typeof zApplyOperationalDataBatchPayload>;

/**
 * Result of applying one operational-data batch. Stored in `operational_data_batch.result` and
 * echoed back verbatim on idempotent re-POST (`alreadyApplied: true`). Because the MVP write is a
 * full replace of the dataset scope, the result describes the replaced scope rather than per-row
 * create/update/delete deltas (PLT-5088 Q3).
 */
export type ApplyOperationalDataBatchResult = {
  batchId: string;
  companyVariantId: string;
  companyId: string;
  replacedScope: {
    dimension: CompanyOperationsDimension;
    operationsMetricType: CompanyOperationsMetricType;
    /** The year the datapoints were written for — always the current year. */
    year: number;
    /** The year of the report the figures came from, when the proposal named one. Audit only. */
    reportYear?: number;
  };
  writtenKeys: string[];
  rowCount: number;
  alreadyApplied?: boolean;
};
